import { Badge, Select, Spinner, useToast } from "@design";
import { useMutation } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  type AiModelResponse,
  adminUpdateTierDefaultMutation,
  errorMessage,
  type ModelTierDefaultResponse,
} from "@/api";
import { SettingsList, SettingsRow } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { AdminPage } from "../AdminPage";
import { useAdminCanEdit } from "../useAdminCanEdit";
import { modelsForTier, orderTiers, sortModels } from "./modelOptions";
import { useAdminModels, useRefreshModels, useTierDefaults } from "./useModelData";

const NONE = "__none__";

function TierControl({
  tier,
  models,
  canEdit,
  saving,
  onChange,
}: Readonly<{
  tier: ModelTierDefaultResponse;
  models: AiModelResponse[];
  canEdit: boolean;
  saving: boolean;
  onChange: (modelId: string | null) => void;
}>) {
  const { t } = useTranslation("adminModels");
  const current = models.find((m) => m.id === tier.modelId);
  if (!canEdit) {
    return (
      <Text as="span" variant="body-sm" tone={tier.modelId ? "primary" : "tertiary"}>
        {current?.name ?? tier.modelName ?? t("defaults.notConfigured")}
      </Text>
    );
  }
  // Enabled models that fit the tier, plus the current pick even if it's since been disabled.
  const choices = sortModels(
    modelsForTier(tier.tier, models).filter((m) => m.isEnabled || m.id === tier.modelId),
    "name",
  );
  return (
    <div className="flex items-center gap-2">
      <Select
        size="md"
        aria-label={t("defaults.modelFor", { tier: tier.label })}
        value={tier.modelId ?? NONE}
        disabled={saving}
        options={[
          { value: NONE, label: t("defaults.notConfigured") },
          ...choices.map((m) => ({
            value: m.id,
            label: m.isDeprecated ? t("defaults.deprecatedOption", { name: m.name }) : m.name,
          })),
        ]}
        onChange={(value) => value !== (tier.modelId ?? NONE) && onChange(value === NONE ? null : value)}
      />
      {saving && <Spinner size="sm" />}
    </div>
  );
}

/** /admin/model-defaults: which model fills each tier; changes save immediately. */
export function ModelDefaultsPage() {
  const { t } = useTranslation("adminModels");
  const toast = useToast();
  const canEdit = useAdminCanEdit();
  const models = useAdminModels();
  const defaults = useTierDefaults();
  const refresh = useRefreshModels();
  const save = useMutation(adminUpdateTierDefaultMutation());

  const change = (tier: ModelTierDefaultResponse, modelId: string | null) =>
    save.mutate(
      { path: { tier: tier.tier }, body: { modelId } },
      {
        onSuccess: () => {
          toast.success(t("defaults.saved"));
          void refresh();
        },
        onError: (error) => toast.error(errorMessage(error, t("defaults.saveFailed"))),
      },
    );

  const tiers = orderTiers(defaults.data?.data.defaults ?? []);
  const savingTier = save.isPending ? save.variables?.path.tier : undefined;

  let body: ReactNode;
  if (defaults.isPending || models.isPending) body = <Spinner />;
  else if (defaults.isError || models.isError) {
    body = (
      <Text tone="error" role="alert">
        {t("defaults.loadFailed")}
      </Text>
    );
  } else {
    body = (
      <SettingsList>
        {tiers.map((tier) => (
          <SettingsRow
            key={tier.tier}
            label={tier.label}
            description={tier.description ?? undefined}
            badge={
              tier.userFacing ? (
                <Badge size="compact" variant="info">
                  {t("defaults.userFacing")}
                </Badge>
              ) : undefined
            }
          >
            <TierControl
              tier={tier}
              models={models.data.data}
              canEdit={canEdit}
              saving={savingTier === tier.tier}
              onChange={(modelId) => change(tier, modelId)}
            />
          </SettingsRow>
        ))}
      </SettingsList>
    );
  }

  return (
    <AdminPage title={t("defaults.title")} subtitle={t("defaults.subtitle")}>
      {body}
    </AdminPage>
  );
}
